import { IUnitSchedule, Schedule } from "@auvious/utils";
import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
} from "@stencil/core";

import { Calendar, CalendarEntry, IThemeOptions } from "../../../models";
import {
  AppConfigService,
  DateTimeFactory,
  ThemeService,
  TranslateService,
} from "../../../services";

enum FocusedDate {
  today,
  tomorrow,
  future,
  never,
}

interface IDatePickerState {
  focusedDay?: FocusedDate;
  date?: Date;
  isCalendarOpen?: boolean;
}

const DEFAULT_DATE_PICKER_STATE: IDatePickerState = {
  focusedDay: FocusedDate.today,
  date: null,
  isCalendarOpen: false,
};

@Component({
  tag: "av-date-picker",
  styleUrl: "av-date-picker.scss",
  shadow: true,
})
export class AvDatePicker implements ComponentInterface {
  @Prop() date: Date;
  @Prop() schedule: Schedule;

  @State() state: IDatePickerState = { ...DEFAULT_DATE_PICKER_STATE };
  @State() themeOptions: IThemeOptions;

  @Event() dateChange: EventEmitter<Date>;

  private today: Date;
  private tomorrow: Date;
  private future: Date;

  // minumum minute interval
  private step = AppConfigService.scheduleIntervalMinutes;

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );
  }

  @Watch("date")
  dateChanged(date: Date) {
    if (date !== this.state.date) {
      this.today?.setHours(date.getHours());
      this.today?.setMinutes(date.getMinutes());
      this.tomorrow?.setHours(date.getHours());
      this.tomorrow?.setMinutes(date.getMinutes());
      this.future?.setHours(date.getHours());
      this.future?.setMinutes(date.getMinutes());
    }
  }

  componentWillLoad() {
    const from = new Date();

    if (
      this.schedule.nextOpenTime(
        from.getFullYear(),
        from.getMonth(),
        from.getDate(),
        from.getHours(),
        from.getMinutes(),
        this.step
      )
    ) {
      this.today = new Date();
      this.today.setHours(this.date.getHours());
      this.today.setMinutes(this.date.getMinutes());
    }

    from.setDate(from.getDate() + 1);

    if (
      this.schedule.nextOpenTime(
        from.getFullYear(),
        from.getMonth(),
        from.getDate(),
        0,
        0,
        this.step
      )
    ) {
      this.tomorrow = new Date(from);
      this.tomorrow.setHours(this.date.getHours());
      this.tomorrow.setMinutes(this.date.getMinutes());
    }

    from.setDate(from.getDate() + 1);

    // find next available time after tomorrow in the next 3 months
    for (let days = 93; !this.future && days; days--) {
      if (
        this.schedule.nextOpenTime(
          from.getFullYear(),
          from.getMonth(),
          from.getDate(),
          0,
          0,
          this.step
        )
      ) {
        this.future = new Date(from);
        this.future.setHours(this.date.getHours());
        this.future.setMinutes(this.date.getMinutes());
      } else {
        from.setDate(from.getDate() + 1);
      }
    }

    if (!this.today && !this.tomorrow && !this.future) {
      // TODO: emit error - no availability
    }

    let focusedDay: FocusedDate;
    const now = new Date();
    const tomorrow = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );

    if (this.date.getTime() < tomorrow.getTime() && this.today) {
      this.dateChange.emit(this.today);
      focusedDay = FocusedDate.today;
    } else if (
      this.date.getTime() < tomorrow.setDate(tomorrow.getDate() + 1) &&
      this.tomorrow
    ) {
      this.dateChange.emit(this.tomorrow);
      focusedDay = FocusedDate.tomorrow;
    } else {
      this.dateChange.emit(this.future);
      focusedDay = FocusedDate.future;
      // change future date if we have set it before
      if (this.date) {
        this.future = new Date(this.date);
      }
    }

    this.setState({ date: this.date, focusedDay });
  }

  private setState(props: IDatePickerState) {
    this.state = { ...this.state, ...props };
  }

  focusDayChanged(focusedDay: FocusedDate) {
    switch (focusedDay) {
      case FocusedDate.today:
        this.setState({ isCalendarOpen: false });
        this.dateChange.emit(this.today);
        break;
      case FocusedDate.tomorrow:
        this.setState({ isCalendarOpen: false });
        this.dateChange.emit(this.tomorrow);
        break;
      case FocusedDate.future:
        this.setState({ isCalendarOpen: !this.state.isCalendarOpen });
        this.dateChange.emit(this.future);
        break;
    }

    this.setState({ focusedDay });
  }

  futureDateChanged({ detail: entry }: CustomEvent<CalendarEntry>) {
    this.future = new Date(
      entry.year,
      entry.month - 1,
      entry.day,
      this.date.getHours(),
      this.date.getMinutes()
    );

    this.setState({ isCalendarOpen: false, date: this.future });
    this.dateChange.emit(this.future);
  }

  get isTodayFocused() {
    return this.state.focusedDay === FocusedDate.today;
  }

  get isTomorrowFocused() {
    return this.state.focusedDay === FocusedDate.tomorrow;
  }

  get isFutureFocused() {
    return this.state.focusedDay === FocusedDate.future;
  }

  private getScheduleUnit(day: Date): IUnitSchedule {
    if (!day) {
      return { open: true, reasons: [] };
    }
    return (
      this.schedule?.getMinutes(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        day.getHours()
      )[day.getMinutes()] || { open: true, reasons: [] }
    );
  }

  render() {
    const optionStyle = {
      backgroundColor: this.themeOptions.colorAccent,
    };

    const dateToday = this.today
      ? DateTimeFactory.create(this.today).format("ddd DD, hh:mm A")
      : null;

    const isTodayClosed = this.today
      ? !this.getScheduleUnit(this.today).open
      : true;

    const isTodayClosedReason =
      this.getScheduleUnit(this.today)?.reasons?.join(" ") ||
      "Closed at this time";

    const dateTomorrow = this.tomorrow
      ? DateTimeFactory.create(this.tomorrow).format("ddd DD, hh:mm A")
      : null;

    const isTomorrowClosed = this.tomorrow
      ? !this.getScheduleUnit(this.tomorrow).open
      : true;

    const isTomorrowClosedReason =
      this.getScheduleUnit(this.tomorrow)?.reasons?.join(" ") ||
      "Closed at this time";

    let minDate: CalendarEntry;
    let selectedDate: CalendarEntry;
    let dateFuture: string;
    let isFutureClosed = true;
    let isFutureClosedReason;

    if (this.future) {
      const futureMin = DateTimeFactory.create().add(2, "day");

      minDate = Calendar.momentToCalendarEntry(futureMin);
      selectedDate = Calendar.dateToCalendarEntry(this.future);

      isFutureClosed = !this.getScheduleUnit(this.future).open;
      isFutureClosedReason =
        this.getScheduleUnit(this.future)?.reasons?.join(" ") ||
        "Closed at this time";

      dateFuture = isFutureClosed
        ? DateTimeFactory.create(this.future).format("ddd DD MMM YYYY")
        : DateTimeFactory.create(this.future).format(
            "ddd DD MMM YYYY, hh:mm A"
          );
    }

    return (
      <div
        class={{
          "av-date-picker": true,
          "av-date-picker-dark": this.themeOptions.darkMode,
        }}
      >
        {dateToday && (
          <button
            class={{
              "av-direction": true,
              "av-date-picker-option": true,
              active: this.isTodayFocused,
            }}
            style={this.isTodayFocused && optionStyle}
            onClick={() => {
              this.focusDayChanged(FocusedDate.today);
            }}
          >
            <span>
              <av-translate>Today</av-translate>
            </span>
            {!isTodayClosed && <small>{dateToday}</small>}
            {isTodayClosed && (
              <small>
                <av-translate>{isTodayClosedReason}</av-translate>
              </small>
            )}
          </button>
        )}

        {dateTomorrow && (
          <button
            class={{
              "av-direction": true,
              "av-date-picker-option": true,
              active: this.isTomorrowFocused,
            }}
            style={this.isTomorrowFocused && optionStyle}
            onClick={() => {
              this.focusDayChanged(FocusedDate.tomorrow);
            }}
          >
            <span>
              <av-translate>Tomorrow</av-translate>
            </span>
            {!isTomorrowClosed && <small>{dateTomorrow}</small>}
            {isTomorrowClosed && (
              <small>
                <av-translate>{isTomorrowClosedReason}</av-translate>
              </small>
            )}
          </button>
        )}

        {dateFuture && (
          <div class="av-date-picker-option-future">
            <button
              class={{
                "av-date-picker-option": true,
                active: this.isFutureFocused,
              }}
              style={this.isFutureFocused && optionStyle}
              onClick={() => {
                this.focusDayChanged(FocusedDate.future);
              }}
            >
              {(this.isFutureFocused && !this.state.date) ||
                (!this.isFutureFocused && (
                  <span>
                    <av-translate>Pick a date</av-translate>
                  </span>
                ))}
              {this.isFutureFocused && !!this.state.date && (
                <span>{dateFuture}</span>
              )}

              {this.isFutureFocused && !!this.state.date && isFutureClosed && (
                <small>
                  <av-translate>{isFutureClosedReason}</av-translate>
                </small>
              )}

              <div class="av-arrow-down"></div>
            </button>
            <av-card
              aria-label={TranslateService.instant("Calendar")}
              role="dialog"
              class="av-card-calendar"
              open={this.state.isCalendarOpen}
            >
              <div slot="card-body">
                <av-calendar
                  minDate={minDate}
                  selectedDate={selectedDate}
                  schedule={this.schedule}
                  onDayChanged={this.futureDateChanged.bind(this)}
                ></av-calendar>
              </div>
            </av-card>
          </div>
        )}
      </div>
    );
  }
}
